import {
    useCallback, useContext, useEffect, useMemo, useRef, useState 
} from 'react';
import {
    GOAL_CHOICES 
} from '../config/types';
import CardContainer from '../components/cardContainer';
import Card from '../components/card';
import ProgressBar from '../components/progressBar';
import {
    Box, Slide, Typography, useTheme 
} from '@mui/material';
import {
    makeStyles 
} from '@mui/styles';
import dataDogLogger from '../../../lib/datadog';
import {
    DiagnosticFormContext 
} from '../../../contexts/form';
import {
    TypographyVariants, TypographyWeights 
} from '../../../figmaTheme';
import SunnysideButton, {
    ButtonSizes, ButtonVariants 
} from '../../../components_sunnyside/button';
import {
    useCookies 
} from 'react-cookie';

type Props = {
    position: number,
    backJump?: number
};

const ENVIRONMENT = process.env.NEXT_PUBLIC_NENV;
const skippyEnvironment = new Set(['development', 'staging']);

const useStyles = makeStyles((theme) => ({
    scrollableContainer: {
        overflow: 'scroll',
        scrollbarWidth: 'none'
    },
    textBox: {
        marginBottom: '16px',
    },
    title: {
        marginBottom: '8px',
    },
    choices: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gridGap: '8px',
        [theme.breakpoints.up('lg')]: {
            gridTemplateColumns: '1fr 1fr 1fr',
        }
    },
    choice: {
        display: 'grid',
        rowGap: '8px',
        borderRadius: '8px',
        minHeight: '45px',
        padding: '12px 0px',
        placeItems: 'center',
        justifyContent: 'center',
        alignContent: 'center',
        transition: 'opacity ease-in-out 0.5s, top ease-in-out 0.3s, left ease-in-out 0.3s, width ease-in-out 0.3s, height ease-in-out 0.3s, border-radius ease-in-out 0.5s, background-color ease-in-out 0.5s, border ease-in-out 0.3s, transform ease-in-out 0.5s',
    },
    choiceIcon: {
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        transition: 'transform ease-in-out 0.5s'
    },
    choiceSubtitle: {
        fontWeight: TypographyWeights.MEDIUM,
        textAlign: 'center'
    },
    button: {
        fontSize: '16px',
        color: theme.palette.white,
        fontWeight: TypographyWeights.MEDIUM,
        padding: '12px 16px',
        borderRadius: '16px',
        width: '100%'
    },
    arrowButton: {
        padding: '16px',
        borderRadius: '50%',
        placeSelf: 'end',
        minWidth: 0,
        lineHeight: 0
    },
    empty: {
        height: '88px',
    },
    skippy: {
        backgroundColor: theme.palette.error.main,
        margin: '0 auto'
    }
}));

export const GoalsChoiceScreen = ({
    position,
    backJump = 1
}: Props): JSX.Element => {
    const formContext = useContext(DiagnosticFormContext);
    const classes = useStyles();
    const theme = useTheme();
    const [cookies] = useCookies();
    const [startTime, setStartTime] = useState(formContext.startTime);

    const initialScreenWidth = useMemo(() => {
        if (typeof window !== 'undefined') {
            return window.innerWidth;
        }
        return 0;
    }, []);
    const initialScreenHeight = useMemo(() => {
        if (typeof window !== 'undefined') {
            return window.innerHeight;
        }
        return 0;
    }, []);
    const [screenWidth, setScreenWidth] = useState(initialScreenWidth);
    const [screenHeight, setScreenHeight] = useState(initialScreenHeight);
    const [firstLoad, setFirstLoad] = useState(true);
    const [otherChoiceSelections, setOtherChoiceSelections] = useState(GOAL_CHOICES.map((choice) => formContext.answers['other_goals']?.includes(choice.key)));
    const [step, setStep] = useState(formContext.answers?.['top_goal'] && formContext.screenDirection === 'right' && otherChoiceSelections.filter(Boolean).length > 1 ? 1 : 0);
    const [topGoal, setTopGoal] = useState(formContext.answers?.['top_goal']);
    const [showButton, setShowButton] = useState(otherChoiceSelections.some(Boolean));
    const [slide, setSlide] = useState(true);
    const [direction, setDirection] = useState(formContext.screenDirection);
    const [contentHeight, setContentHeight] = useState(null);
    const TEXT = useMemo(() => [
        (
            <Box key={0}>
                <Typography
                    variant={TypographyVariants.H3}
                    className={classes.title}
                    dangerouslySetInnerHTML={{
                        __html: `${formContext.answers?.['your_name'] ? `Welcome, ${formContext.answers?.['your_name']}! What` : 'First, the basics: what'} <span style="color: ${theme.palette.primary.main}; font-weight: ${TypographyWeights.BOLD}">motivates you</span> to want to improve your drinking habits?` 
                    }}
                />
                <Typography
                    variant={TypographyVariants.H6}
                >
                    {'Choose all that apply:'}
                </Typography>
            </Box>
        ),
        (
            <Box key={1}>
                <Typography
                    variant={TypographyVariants.H3}
                    className={classes.title}
                    dangerouslySetInnerHTML={{
                        __html: `Thanks for sharing! Which of these goals <span style="color: ${theme.palette.primary.main}; font-weight: ${TypographyWeights.BOLD}">feels most important</span> to you right now?` 
                    }}
                />
                <Typography
                    variant={TypographyVariants.H6}
                >
                    {'Choose one:'}
                </Typography>
            </Box>
        ),
        (
            <Box key={2} />
        )
    ], [contentHeight]);
    const [content, setContent] = useState(TEXT[step]);

    const contentContainer = useRef(null);
    const scrollableContainer = useRef(null);

    const onSubmitOtherGoalChoices = useCallback(() => {
        setStep(1);
        setShowButton(!!topGoal);

        setSlide(false);
        setDirection('right');
        if (topGoal) {
            const otherGoalChoices = otherChoiceSelections.map((selection, index) => (
                selection && GOAL_CHOICES[index].key
            )).filter(Boolean);
            if (!otherGoalChoices.includes(topGoal)) {
                setTopGoal(null);
                setShowButton(false);
            }
        }
        if (scrollableContainer.current) scrollableContainer.current.scrollTo({
            top: 0,
            behaviour: 'smooth' 
        });
    }, []);

    const onChoiceClick = useCallback((e) => {
        if (step === 1) {
            setStep(2);
            setTopGoal(e?.currentTarget?.dataset?.key || topGoal);

            setSlide(false);
            setDirection('right');
            if (scrollableContainer.current) scrollableContainer.current.scrollTo({
                top: 0,
                behaviour: 'smooth' 
            });
            return;
        } else if (step === 0) {
            const newOtherChoiceSelections = otherChoiceSelections;
            otherChoiceSelections[e.currentTarget.dataset.index] = !otherChoiceSelections[e.currentTarget.dataset.index];
            setOtherChoiceSelections([...newOtherChoiceSelections]);
            setShowButton(otherChoiceSelections.some(Boolean));
        }
    }, [step, otherChoiceSelections]);

    const goBackToOtherGoalSelection = useCallback(() => {
        setStep(0);
        setShowButton(otherChoiceSelections.some(Boolean));

        setStartTime(null);
        setSlide(false);
        setDirection('left');
        if (scrollableContainer.current) scrollableContainer.current.scrollTo({
            top: 0,
            behaviour: 'smooth' 
        });
    }, []);

    useEffect(() => {
        if (slide) return;

        setTimeout(() => {
            setContent(TEXT[step]);
        }, 500);
    }, [slide]);

    useEffect(() => {
        setDirection(step === 0 ? 'right' : 'left');
        setSlide(true);
    }, [content]);

    useEffect(() => {
        if (step < 2) {
            formContext.fireTrackingEvents(formContext, step === 0 ? 'other_goals' : 'top_goal', cookies);
            setStartTime(new Date().getTime());
            if (!firstLoad && otherChoiceSelections.filter(Boolean).length === 1) {
                setStep(2);
                const topGoalChoice = GOAL_CHOICES[otherChoiceSelections.findIndex(Boolean)]?.key || topGoal;
                setTopGoal(topGoalChoice);

                setSlide(false);
                setDirection('right');
                if (scrollableContainer.current) scrollableContainer.current.scrollTo({
                    top: 0,
                    behaviour: 'smooth' 
                });
            }
            return;
        }

        if (step >= 2 && step < 5) {
            setTimeout(() => {
                setStep(step + 1);
            }, 500);
            return;
        }

        setTimeout(() => {
            formContext.setAnswers({
                top_goal: topGoal,
                other_goals: otherChoiceSelections.map((selection, index) => (
                    selection && GOAL_CHOICES[index].key
                )).filter(Boolean)
            });
            formContext.goNext();
        }, 1000);
    }, [step]);

    useEffect(() => {
        if (typeof window === 'undefined') return;

        const resizeListener = () => {
            setScreenWidth(window.innerWidth);
            setScreenHeight(window.innerHeight);
        };
        window.addEventListener('resize', resizeListener);

        setFirstLoad(false);
        return () => {
            window.removeEventListener('resize', resizeListener);
        };
    }, []);

    useEffect(() => {
        if (!startTime) return;

        const endTime = new Date().getTime();
        const diff = endTime - startTime;

        dataDogLogger(
            'info',
            `Diagnostic screen ${step === 0 ? 'other_goals' : 'top_goal'} rendered in ${diff}ms`,
            {
                frontendMetric: diff,
                url: step === 0 ? 'other_goals' : 'top_goal',
                rawData: {
                    formId: formContext.formId
                }
            }
        );
    }, [startTime]);

    useEffect(() => {
        if (contentContainer.current) setContentHeight(contentContainer.current.clientHeight);
    }, [contentContainer.current]);

    let order = useMemo(() => 0, []);

    const onBackClick = () => {
        formContext.goBack(backJump);
    };

    return (
        <CardContainer
            sectionKey={'goals'}
            goBack={step === 0 ? onBackClick : step === 1 ? goBackToOtherGoalSelection : null}
            goNext={showButton ? step === 0 ? onSubmitOtherGoalChoices : onChoiceClick : false}
            hasGradient
        >
            <Card>
                <ProgressBar
                    currentPosition={position + (step === 0 ? 0 : step === 1 ? 1 : 2)}
                    sectionKey={'goals'}
                />
                <Slide
                    in={true}
                    direction={formContext.screenDirection}
                    timeout={500}
                    mountOnEnter
                    unmountOnExit
                >
                    <Box
                        ref={scrollableContainer}
                        className={classes.scrollableContainer}
                    >
                        {formContext.skippySkipsGoals && (skippyEnvironment.has(ENVIRONMENT)) && (
                            <SunnysideButton
                                buttonVariant={ButtonVariants.PRIMARY}
                                buttonSize={ButtonSizes.LARGE}
                                onClick={formContext.skippySkipsGoals}
                                className={classes.skippy}
                            >
                                <Typography
                                    style={{
                                        fontWeight: TypographyWeights.BOLD,
                                        color: theme.palette.white
                                    }}
                                >
                                    SKIPPY
                                </Typography>
                            </SunnysideButton>
                        )}
                        <Slide
                            in={slide}
                            direction={direction}
                            timeout={250}
                            mountOnEnter
                        >
                            <Box
                                ref={contentContainer}
                                className={classes.textBox}
                                style={{
                                    height: contentHeight 
                                }}
                            >
                                {content}
                            </Box>
                        </Slide>
                        <Box
                            className={classes.choices}
                            style={{
                                placeItems: step > 1 ? 'center' : 'center' 
                            }}
                        >
                            {GOAL_CHOICES.map((choice, index) => {
                                const pointerEvents = step === 0 || step === 1 && otherChoiceSelections[index] ? 'auto' : 'none';
                                const padding = `0 ${choice.padding}`;
                                const gridTemplateRows = step >= 4 && choice.key === topGoal ? '20px' : '25% auto';
                                const opacity = step === 0 || (step >= 1 && step < 3 && otherChoiceSelections[index]) || (step >= 3 && choice.key === topGoal) ? 1 : 0;
                                const backgroundColor = step >= 1 && choice.key === topGoal ? theme.palette.primary.main : theme.palette.primary.light;
                                const border = step >= 1 && choice.key === topGoal ? `2px solid ${theme.palette.primary.main}` : otherChoiceSelections[index] ? `2px solid ${theme.palette.primary.dark}` : `2px solid ${theme.palette.primary.light}`;
                                const borderRadius = step >= 4 && choice.key === topGoal ? '50%' : '8px';
                                const transform = step === 5 && choice.key === topGoal ? 'scale(0)' : 'scale(1)';
                                const iconTransform = step >= 4 && choice.key === topGoal ? 'scale(1.5)' : 'scale(1)';
                                const fill = step >= 1 && choice.key === topGoal ? theme.palette.white : theme.palette.primary.dark;
                                const color = step >= 1 && choice.key === topGoal ? theme.palette.white : theme.palette.primary.dark;
                                const isSubtitleVisible = step >= 4 && choice.key === topGoal ? false : true;

                                const numColumns = screenWidth >= 1200 ? 3 : 2;

                                let left = '0';
                                let top = '0';

                                if (step >= 1 && step < 3 && otherChoiceSelections[index]) {
                                    top = `calc(${((Math.floor(order / numColumns)) - Math.floor(index / numColumns)) * 100}% + ${((Math.floor(order / numColumns)) - Math.floor(index / numColumns)) * 8}px)`;
                                    left = `calc(${((order % numColumns) - (index % numColumns)) * 100}% + ${((order % numColumns) - (index % numColumns)) * 8}px)`;
                                    order++;
                                } else if (step >= 3 && otherChoiceSelections[index]) {
                                    if (topGoal === choice.key) {
                                        top = `calc(${(-Math.floor(index / numColumns)) * 100}% + ${(-Math.floor(index / numColumns)) * 8}px)`;
                                        left = `calc(${((screenWidth >= 1200 ? 1 : 0.5) - (index % numColumns)) * 100}% + ${((screenWidth >= 1200 ? 1 : 0.5) - (index % numColumns)) * 8}px)`;
                                    } else {
                                        top = `calc(${((Math.floor(order / numColumns)) - Math.floor(index / numColumns)) * 100}% + ${((Math.floor(order / numColumns)) - Math.floor(index / numColumns)) * 8}px)`;
                                        left = `calc(${((order % numColumns) - (index % numColumns)) * 100}% + ${((order % numColumns) - (index % numColumns)) * 8}px)`;
                                    }
                                    order++;
                                }

                                return (
                                    <Box
                                        key={choice.key}
                                        data-key={choice.key}
                                        data-index={index}
                                        className={classes.choice}
                                        onClick={onChoiceClick}
                                        style={{
                                            width: step >= 4 && choice.key === topGoal ? '58px' : '100%',
                                            height: step >= 4 && choice.key === topGoal ? '58px' : '100%',
                                            gridTemplateRows,
                                            position: 'relative',
                                            left,
                                            top,
                                            opacity,
                                            pointerEvents,
                                            backgroundColor,
                                            border,
                                            borderRadius,
                                            transform,
                                        }}
                                    >
                                        {choice.icon && (
                                            <Box
                                                className={classes.choiceIcon}
                                                style={{
                                                    transform: iconTransform,
                                                    fill 
                                                }}
                                            >
                                                {choice?.icon}
                                            </Box>
                                        )}
                                        {choice.subtitle && isSubtitleVisible && (
                                            <Typography
                                                variant={TypographyVariants.H6}
                                                className={classes.choiceSubtitle}
                                                style={{
                                                    color,
                                                    padding 
                                                }}
                                            >
                                                {choice.subtitle}
                                            </Typography>
                                        )}
                                    </Box>
                                );
                            })}
                            {screenHeight < 900 && ([0, 1, 2, 3, 4, 5].map((index) => (
                                <Box
                                    key={index}
                                    className={classes.empty}
                                />
                            )))}
                        </Box>
                    </Box>
                </Slide>
            </Card>
        </CardContainer>
    );
};

export default GoalsChoiceScreen;
